/**
 * Potansiyel müşteriler (v0.1.90): every company the lead engine found, on one live page.
 * The summary board (counts, mails, cost), the lead jobs (running now, planned, last runs),
 * groups (kind, production, stage, market, source, found day, decision) whose chips filter the
 * list, the filters and sort, the Excel export of the rows on screen, and the list itself:
 * tapping a company opens how it was found (every search, day and provider), why it is a
 * prospect, why the gate verified it or not, the evidence, the channels, the mails sent and
 * its stage history. Read only; nothing here contacts anyone.
 */

import { type ReactNode, useEffect, useId, useMemo, useState } from 'react';
import {
  BLOCK_REASON_TR,
  basisText,
  CHANNEL_LABELS_TR,
  CHANNEL_STATUS_TR,
  CLAIM_LABELS_TR,
  COUNTRY_LABELS_TR,
  DECIDER_LABELS_TR,
  DELIVERY_MODE_TR,
  DOMAIN_CHECK_LABELS_TR,
  HIT_FILTER_TR,
  HIT_RESOLUTION_TR,
  JOB_STATUS_TR,
  KIND_ORDER,
  KIND_WHY_TR,
  MESSAGE_STATUS_TR,
  ORIGIN_LABELS_TR,
  OUTREACH_ARM_TR,
  OUTREACH_REASON_TR,
  PRODUCTION_ORDER,
  PROVIDER_LABELS_TR,
  type ProspectDetail,
  type ProspectEstimate,
  type ProspectKind,
  type ProspectReach,
  type ProspectRow,
  type ProspectsView,
  STAGE_LABELS_TR,
  STAGE_ORDER,
  UNFIT_LABELS_TR,
} from '../api/prospects.js';
import { usePanel } from '../app/context.js';
import { type ProspectsState, useProspects } from '../app/prospects.js';
import {
  EMPTY_FILTERS,
  filterProspects,
  GROUP_DIM_LABELS_TR,
  GROUP_DIMS,
  type GroupDim,
  groupKey,
  groupLabel,
  groupProspects,
  isQualified,
  MAIL_FILTER_LABELS_TR,
  MAIL_FILTERS,
  type MailFilter,
  type ProspectFilters,
  prospectsCsv,
  SORT_KEYS,
  SORT_LABELS_TR,
  type SortKey,
  sortProspects,
  sourceKey,
} from '../app/prospects-filter.js';
import {
  formatClock,
  formatClockSeconds,
  formatDayMonth,
  formatNumber,
  formatPct,
  formatRelativeDay,
  formatUsd,
  istanbulIsoDate,
} from '../lib/format.js';
import { Icon } from './icons.js';
import { LiveDot, Meter } from './primitives.js';
import { copyText } from './sheets.js';

const PAGE_SIZE = 100;

function providerLabel(key: string | null): string {
  if (key === null) return 'bilinmiyor';
  return PROVIDER_LABELS_TR[key] ?? key;
}

function countryLabel(code: string | null): string {
  if (code === null) return 'Ülke yok';
  return COUNTRY_LABELS_TR[code] ?? code;
}

function when(at: number | null, now: number): string {
  return at === null ? 'tarih yok' : formatRelativeDay(at, now);
}

function stamp(at: number | null): string {
  return at === null ? '' : `${formatDayMonth(at)} ${formatClock(at)}`;
}

/** Tone of a decision chip: never colour alone, the word is always on the chip. */
function verdictTone(verdict: string | null): 'ok' | 'warn' | 'bad' | 'none' {
  if (verdict === null) return 'none';
  if (verdict === 'KABUL' || verdict === 'DOGRULANDI' || verdict === 'NITELIKLI') return 'ok';
  if (verdict === 'INCELE') return 'warn';
  return 'bad';
}

function stageTone(stage: string): 'ok' | 'warn' | 'bad' | 'none' {
  if (stage === 'uygun_degil') return 'bad';
  if (stage === 'bulundu') return 'none';
  if (stage === 'nitelikli') return 'warn';
  return 'ok';
}

function productionTone(value: string): 'ok' | 'warn' | 'bad' | 'none' {
  if (value === 'yes' || value === 'likely') return 'ok';
  if (value === 'possible') return 'warn';
  if (value === 'unlikely' || value === 'no') return 'bad';
  return 'none';
}

function download(rows: readonly ProspectRow[], now: number): void {
  const blob = new Blob([prospectsCsv(rows)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `potansiyel-musteriler-${istanbulIsoDate(now)}.csv`;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ---- live bar -------------------------------------------------------------------------

function LiveBar({
  state,
  shown,
  onReload,
  onExport,
}: {
  state: ProspectsState;
  shown: number;
  onReload: () => void;
  onExport: () => void;
}) {
  return (
    <div className="pm-live">
      <p className="pm-live__line" role="status">
        <LiveDot hollow={state.error !== null} />
        <span>
          <strong>Canlı</strong> · her 20 saniyede yenilenir
          {state.refreshedAt !== null ? (
            <>
              {' '}
              · son okuma <span className="mono">{formatClockSeconds(state.refreshedAt)}</span>
            </>
          ) : null}
          {state.loading ? ' · okunuyor…' : ''}
          {state.error !== null ? ` · son okuma başarısız: ${state.error}` : ''}
        </span>
      </p>
      <div className="pm-live__actions">
        <button type="button" className="btn btn--secondary" onClick={onReload} disabled={state.loading}>
          <Icon name="refresh-cw" size={16} />
          Yenile
        </button>
        <button type="button" className="btn btn--primary" onClick={onExport} disabled={shown === 0}>
          <Icon name="cloud-download" size={16} />
          Excel'e aktar ({formatNumber(shown)} firma)
        </button>
      </div>
    </div>
  );
}

// ---- summary board ----------------------------------------------------------------------

function Tile({ label, value, note }: { label: string; value: string; note?: string | undefined }) {
  return (
    <div className="pm-tile">
      <span className="pm-tile__label">{label}</span>
      <span className="pm-tile__value mono">{value}</span>
      {note ? <span className="pm-tile__note">{note}</span> : null}
    </div>
  );
}

/** An estimate rounded the way it is meant: tens below a thousand, hundreds above. */
export function roughCount(value: number): string {
  const step = value >= 1000 ? 100 : 10;
  return formatNumber(Math.round(value / step) * step);
}

/** Regions the state-wide maker search has reached, of the map's regions (v0.1.126). */
function searchedRegions(reach: ProspectReach): { searched: number; total: number; missing: string[] } {
  const regions = reach.cells.byRegion;
  const missing = regions.filter((region) => (region.makerQueries ?? 0) === 0).map((region) => region.name);
  return { searched: regions.length - missing.length, total: regions.length, missing };
}

function EstimateTile({
  label,
  estimate,
  searched,
}: {
  label: string;
  estimate: ProspectEstimate;
  searched?: { searched: number; total: number } | undefined;
}) {
  if (estimate.estimate === null) {
    return <Tile label={label} value="?" note="tahmin için web araması ve listelerin en az 7 ortak firması gerekir" />;
  }
  return (
    <Tile
      label={`${label} (en az)`}
      value={`~${roughCount(estimate.estimate)}`}
      note={`bulunan ${formatNumber(estimate.found)} · ${formatPct(estimate.found / estimate.estimate)}${
        searched !== undefined ? ` · arama ${searched.searched}/${searched.total} bölgede` : ''
      }`}
    />
  );
}

function ReachBoard({ reach }: { reach: ProspectReach }) {
  const q = reach.estimate.qualified;
  const regions = searchedRegions(reach);
  return (
    <div className="pm-reach">
      <div className="pm-reach__col">
        <h3 className="pm-jobs__head">Firmalar nereden geldi</h3>
        <table className="pm-table">
          <thead>
            <tr>
              <th scope="col">Kaynak</th>
              <th scope="col" className="num">
                Firma
              </th>
              <th scope="col" className="num">
                Nitelikli
              </th>
            </tr>
          </thead>
          <tbody>
            {reach.origins
              .filter((origin) => origin.firms > 0)
              .map((origin) => (
                <tr key={origin.key}>
                  <td>
                    {ORIGIN_LABELS_TR[origin.key] ?? origin.key}
                    {origin.key === 'cells' ? ` (${formatNumber(reach.cells.scanned)} hücre)` : ''}
                  </td>
                  <td className="num mono">{formatNumber(origin.firms)}</td>
                  <td className="num mono">{formatNumber(origin.qualified)}</td>
                </tr>
              ))}
          </tbody>
        </table>
        <p className="pm-muted">
          Tahmin, web aramasının ve fuar ya da rehber listelerinin birlikte bulduğu firmalara göre hesaplanır (
          {formatNumber(q.web)} aramada, {formatNumber(q.listed)} listede, {formatNumber(q.both)} ikisinde birden
          bulunan nitelikli firma). İki yol da büyük ve görünür firmaları daha kolay bulur; sitesi ve fuar standı
          olmayan küçük atölyeler ikisinde de yoktur. Bu yüzden sayı bir alt sınırdır ve yeni firma bulundukça kendini
          günceller.
          {regions.missing.length > 0
            ? ` Eyalet çapında üretici araması şimdiye kadar ${regions.searched} / ${regions.total} bölgede yapıldı; ${regions.missing.join(', ')} arandıkça tahmin büyüyecek.`
            : ''}
        </p>
      </div>
      <div className="pm-reach__col">
        <h3 className="pm-jobs__head">Harita kapsamı</h3>
        <ul className="pm-list">
          {reach.cells.byRegion.map((region) => (
            <li key={region.region} className="pm-reach__region">
              <span className="pm-reach__name">{region.name}</span>
              <span className="mono">
                {formatNumber(region.scanned)} / {formatNumber(region.total)}
              </span>
              {region.makerQueries !== undefined ? (
                <span className="pm-muted pm-reach__search">
                  {region.makerQueries > 0
                    ? `üretici araması: ${formatNumber(region.makerQueries)} sorgu`
                    : 'üretici araması henüz yapılmadı'}
                </span>
              ) : null}
              <Meter
                value={region.total > 0 ? region.scanned / region.total : 0}
                height={4}
                label={`${region.name} taranan hücre oranı`}
              />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function SummaryBoard({ view }: { view: ProspectsView }) {
  const s = view.summary;
  const c = view.costs;
  const costToday = c.scanTodayMicroUsd + c.verifyTodayMicroUsd;
  const costTotal = c.scanTotalMicroUsd + c.verifyTotalMicroUsd;
  const perFirm = s.total > 0 ? Math.round(costTotal / s.total) : 0;
  const waiting = Math.max(0, s.total - s.decided);
  return (
    <section className="pm-block" aria-labelledby="pm-ozet">
      <h2 id="pm-ozet" className="pm-block__title">
        Özet
      </h2>
      <div className="pm-tiles">
        <Tile
          label="Toplam firma"
          value={formatNumber(s.total)}
          note={s.foundToday > 0 ? `bugün +${formatNumber(s.foundToday)}` : 'bugün yeni yok'}
        />
        <Tile
          label="Nitelikli ve üstü"
          value={formatNumber(s.qualified)}
          note={s.total > 0 ? `${formatPct(s.qualified / s.total)} oranında` : undefined}
        />
        {view.reach ? (
          <>
            <Tile
              label="Harita kapsamı"
              value={`${formatNumber(view.reach.cells.scanned)} / ${formatNumber(view.reach.cells.total)}`}
              note={`hücrelerin ${formatPct(
                view.reach.cells.total > 0 ? view.reach.cells.scanned / view.reach.cells.total : 0,
                1,
              )} tarandı`}
            />
            <EstimateTile
              label="Tahmini nitelikli"
              estimate={view.reach.estimate.qualified}
              searched={searchedRegions(view.reach)}
            />
            <EstimateTile label="Tahmini firma" estimate={view.reach.estimate.firms} />
          </>
        ) : null}
        <Tile label="Kimliği doğrulanan" value={formatNumber(s.verified)} note="kampanyaya girebilir" />
        <Tile
          label="Karar verilen"
          value={formatNumber(s.decided)}
          note={`bugün ${formatNumber(s.decidedToday)} · sırada ${formatNumber(waiting)}`}
        />
        <Tile label="E-postası bulunan" value={formatNumber(s.withEmail)} />
        <Tile label="Mail giden" value={formatNumber(s.mailed)} note={`cevap veren ${formatNumber(s.replied)}`} />
        <Tile
          label="Maliyet bugün"
          value={formatUsd(costToday)}
          note={`tarama ${formatUsd(c.scanTodayMicroUsd)} · doğrulama ${formatUsd(c.verifyTodayMicroUsd)}`}
        />
        <Tile label="Toplam maliyet" value={formatUsd(costTotal)} note={`firma başına ${formatUsd(perFirm, 3)}`} />
      </div>
      {view.reach ? <ReachBoard reach={view.reach} /> : null}
    </section>
  );
}

// ---- jobs -----------------------------------------------------------------------------

function JobsPanel({ view, now }: { view: ProspectsView; now: number }) {
  const jobs = view.jobs;
  const scanRatio = jobs.cells.total > 0 ? jobs.cells.scanned / jobs.cells.total : 0;
  return (
    <section className="pm-block" aria-labelledby="pm-isler">
      <h2 id="pm-isler" className="pm-block__title">
        İşler
      </h2>
      <div className="pm-jobs">
        <div className="pm-jobs__col">
          <h3 className="pm-jobs__head">Şu an çalışan</h3>
          {jobs.running.length === 0 ? (
            <p className="pm-muted">Şu an çalışan tarama ya da doğrulama yok.</p>
          ) : (
            <ul className="pm-list">
              {jobs.running.map((run) => (
                <li key={`${run.kind}-${run.startedAt ?? 0}`} className="pm-run">
                  <span className="pm-run__title">
                    <LiveDot /> {run.kind === 'tarama' ? 'Tarama' : 'Doğrulama'} çalışıyor
                  </span>
                  <span className="pm-run__meta">
                    başladı {run.startedAt === null ? '?' : formatClock(run.startedAt)} ·{' '}
                    <span className="mono">
                      {formatNumber(run.processed)} / {formatNumber(run.selected)}
                    </span>{' '}
                    {run.kind === 'tarama' ? 'hücre' : 'firma'}
                    {run.heartbeatAt !== null ? ` · son sinyal ${formatClockSeconds(run.heartbeatAt)}` : ''}
                  </span>
                  <Meter
                    value={run.selected > 0 ? run.processed / run.selected : 0}
                    height={6}
                    label={`${run.kind} ilerlemesi`}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="pm-jobs__col">
          <h3 className="pm-jobs__head">Planlanan</h3>
          <ul className="pm-list">
            {[...jobs.next]
              .sort((a, b) => (a.at ?? Number.MAX_SAFE_INTEGER) - (b.at ?? Number.MAX_SAFE_INTEGER))
              .map((job) => (
                <li key={job.kind} className="pm-next">
                  <span className="pm-next__when mono">{job.at === null ? 'kapalı' : when(job.at, now)}</span>
                  <span className="pm-next__what">
                    <strong>{job.kind === 'tarama' ? 'Tarama' : 'Doğrulama'}</strong> · {job.what}
                  </span>
                </li>
              ))}
          </ul>
          <div className="pm-cells">
            <span>
              Hücreler: <span className="mono">{formatNumber(jobs.cells.scanned)}</span> /{' '}
              <span className="mono">{formatNumber(jobs.cells.total)}</span> tarandı · bu hafta{' '}
              <span className="mono">
                {jobs.cells.week}/{jobs.cells.quota}
              </span>
            </span>
            <Meter value={scanRatio} height={4} label="Taranan hücre oranı" />
            <span className="pm-muted">
              Doğrulama sırasında <span className="mono">{formatNumber(jobs.queue.due)}</span> firma var; bunların{' '}
              <span className="mono">{formatNumber(jobs.queue.waiting)}</span> tanesi hâlâ "bulundu" aşamasında.
            </span>
          </div>
        </div>
        <div className="pm-jobs__col pm-jobs__col--wide">
          <h3 className="pm-jobs__head">Son işler</h3>
          {jobs.recent.length === 0 ? (
            <p className="pm-muted">Henüz biten iş yok.</p>
          ) : (
            <div className="pm-scroll">
              <table className="pm-table">
                <thead>
                  <tr>
                    <th scope="col">Başladı</th>
                    <th scope="col">İş</th>
                    <th scope="col">Durum</th>
                    <th scope="col">Süre</th>
                    <th scope="col" className="num">
                      Seçilen
                    </th>
                    <th scope="col" className="num">
                      İşlenen
                    </th>
                    <th scope="col" className="num">
                      Hata
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.recent.map((run) => {
                    const minutes =
                      run.startedAt !== null && run.finishedAt !== null
                        ? Math.max(1, Math.round((run.finishedAt - run.startedAt) / 60_000))
                        : null;
                    return (
                      <tr key={`${run.kind}-${run.startedAt ?? 0}`}>
                        <td className="mono">{when(run.startedAt, now)}</td>
                        <td>{run.kind === 'tarama' ? 'Tarama' : 'Doğrulama'}</td>
                        <td>
                          {JOB_STATUS_TR[run.status] ?? run.status}
                          {run.note ? <span className="pm-muted pm-clip"> · {run.note}</span> : null}
                        </td>
                        <td className="mono">{minutes === null ? '' : `${minutes} dk`}</td>
                        <td className="num mono">{formatNumber(run.selected)}</td>
                        <td className="num mono">{formatNumber(run.processed)}</td>
                        <td className="num mono">{formatNumber(run.errors)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// ---- groups ---------------------------------------------------------------------------

function GroupsPanel({
  rows,
  filters,
  dim,
  onDim,
  onPick,
}: {
  rows: readonly ProspectRow[];
  filters: ProspectFilters;
  dim: GroupDim;
  onDim: (dim: GroupDim) => void;
  onPick: (dim: GroupDim, key: string | null) => void;
}) {
  // Counts ignore this dimension's own pick, so the other chips stay visible to switch to.
  const base = useMemo(() => filterProspects(rows, filters, dim), [rows, filters, dim]);
  const groups = useMemo(() => groupProspects(base, dim), [base, dim]);
  const top = groups.reduce((max, group) => Math.max(max, group.count), 0);
  const picked = filters.picks[dim];
  return (
    <section className="pm-block" aria-labelledby="pm-gruplar">
      <h2 id="pm-gruplar" className="pm-block__title">
        Gruplar
      </h2>
      <div className="pm-dims" role="tablist" aria-label="Gruplama">
        {GROUP_DIMS.map((item) => (
          <button
            key={item}
            type="button"
            role="tab"
            aria-selected={item === dim}
            className={item === dim ? 'pm-dim pm-dim--on' : 'pm-dim'}
            onClick={() => onDim(item)}
          >
            {GROUP_DIM_LABELS_TR[item]}
            {filters.picks[item] !== undefined ? <span className="pm-dim__dot" title="filtre açık" /> : null}
          </button>
        ))}
      </div>
      <ul className="pm-groups" aria-label="Gruplar">
        {groups.map((group) => {
          const on = picked === group.key;
          return (
            <li key={group.key}>
              <button
                type="button"
                className={on ? 'pm-group pm-group--on' : 'pm-group'}
                aria-pressed={on}
                onClick={() => onPick(dim, on ? null : group.key)}
              >
                <span className="pm-group__label">{group.label}</span>
                <span className="pm-group__count mono">{formatNumber(group.count)}</span>
                <span className="pm-group__bar" aria-hidden="true">
                  <span style={{ width: `${top > 0 ? (group.count / top) * 100 : 0}%` }} />
                </span>
                <span className="pm-group__meta">
                  nitelikli {formatNumber(group.qualified)} · e-posta {formatNumber(group.withEmail)} · mail{' '}
                  {formatNumber(group.mailed)} · ort. uyum {group.avgFit}
                </span>
                {dim === 'kind' ? (
                  <span className="pm-group__why">{KIND_WHY_TR[group.key as ProspectKind] ?? ''}</span>
                ) : null}
              </button>
            </li>
          );
        })}
      </ul>
      {groups.length === 0 ? <p className="pm-muted">Bu filtrelerle grup yok.</p> : null}
    </section>
  );
}

// ---- filters --------------------------------------------------------------------------

function PickSelect({
  label,
  dim,
  rows,
  filters,
  onPick,
  order,
}: {
  label: string;
  dim: GroupDim;
  rows: readonly ProspectRow[];
  filters: ProspectFilters;
  onPick: (dim: GroupDim, key: string | null) => void;
  order?: readonly string[];
}) {
  const id = useId();
  const options = useMemo(() => {
    const seen = new Map<string, string>();
    for (const row of rows) {
      const key = groupKey(row, dim);
      if (!seen.has(key)) seen.set(key, groupLabel(dim, key, row));
    }
    const list = [...seen.entries()];
    if (order !== undefined) {
      const rank = (key: string) => (order.indexOf(key) < 0 ? order.length : order.indexOf(key));
      return list.sort((a, b) => rank(a[0]) - rank(b[0]));
    }
    if (dim === 'day') return list.sort((a, b) => b[0].localeCompare(a[0]));
    return list.sort((a, b) => a[1].localeCompare(b[1], 'tr'));
  }, [rows, dim, order]);
  return (
    <div className="pm-field">
      <label htmlFor={id} className="pm-field__label">
        {label}
      </label>
      <select
        id={id}
        className="pm-select"
        value={filters.picks[dim] ?? ''}
        onChange={(event) => onPick(dim, event.target.value === '' ? null : event.target.value)}
      >
        <option value="">Hepsi</option>
        {options.map(([key, text]) => (
          <option key={key} value={key}>
            {text}
          </option>
        ))}
      </select>
    </div>
  );
}

function FiltersBar({
  rows,
  filters,
  shown,
  onChange,
  onPick,
}: {
  rows: readonly ProspectRow[];
  filters: ProspectFilters;
  shown: number;
  onChange: (patch: Partial<ProspectFilters>) => void;
  onPick: (dim: GroupDim, key: string | null) => void;
}) {
  const searchId = useId();
  const mailId = useId();
  const fitId = useId();
  const sortId = useId();
  const active =
    filters.q.trim() !== '' ||
    Object.values(filters.picks).some((value) => value !== undefined) ||
    filters.mail !== 'all' ||
    filters.fitMin > 0;
  return (
    <section className="pm-block" aria-labelledby="pm-filtreler">
      <h2 id="pm-filtreler" className="pm-block__title">
        Firmalar{' '}
        <span className="pm-block__count mono">
          {formatNumber(shown)} / {formatNumber(rows.length)}
        </span>
      </h2>
      <div className="pm-filters">
        <div className="pm-field pm-field--search">
          <label htmlFor={searchId} className="pm-field__label">
            Ara
          </label>
          <input
            id={searchId}
            type="search"
            className="pm-input"
            placeholder="Firma, alan adı, yer, arama sorgusu, neden…"
            value={filters.q}
            onChange={(event) => onChange({ q: event.target.value })}
          />
        </div>
        <PickSelect label="Aşama" dim="stage" rows={rows} filters={filters} onPick={onPick} order={STAGE_ORDER} />
        <PickSelect label="Tür" dim="kind" rows={rows} filters={filters} onPick={onPick} order={KIND_ORDER} />
        <PickSelect
          label="Üretim"
          dim="production"
          rows={rows}
          filters={filters}
          onPick={onPick}
          order={PRODUCTION_ORDER}
        />
        <PickSelect label="Pazar" dim="market" rows={rows} filters={filters} onPick={onPick} />
        <PickSelect label="Kaynak" dim="source" rows={rows} filters={filters} onPick={onPick} />
        <PickSelect label="Bulunduğu gün" dim="day" rows={rows} filters={filters} onPick={onPick} />
        <PickSelect label="Karar" dim="decision" rows={rows} filters={filters} onPick={onPick} />
        <div className="pm-field">
          <label htmlFor={mailId} className="pm-field__label">
            Mail
          </label>
          <select
            id={mailId}
            className="pm-select"
            value={filters.mail}
            onChange={(event) => onChange({ mail: event.target.value as MailFilter })}
          >
            {MAIL_FILTERS.map((item) => (
              <option key={item} value={item}>
                {MAIL_FILTER_LABELS_TR[item]}
              </option>
            ))}
          </select>
        </div>
        <div className="pm-field">
          <label htmlFor={fitId} className="pm-field__label">
            En az uyum <span className="mono">{filters.fitMin}</span>
          </label>
          <input
            id={fitId}
            type="range"
            min={0}
            max={100}
            step={5}
            className="pm-range"
            value={filters.fitMin}
            onChange={(event) => onChange({ fitMin: Number(event.target.value) })}
          />
        </div>
        <div className="pm-field">
          <label htmlFor={sortId} className="pm-field__label">
            Sıralama
          </label>
          <select
            id={sortId}
            className="pm-select"
            value={filters.sort}
            onChange={(event) => onChange({ sort: event.target.value as SortKey })}
          >
            {SORT_KEYS.map((item) => (
              <option key={item} value={item}>
                {SORT_LABELS_TR[item]}
              </option>
            ))}
          </select>
        </div>
        {active ? (
          <button
            type="button"
            className="btn btn--secondary pm-filters__clear"
            onClick={() => onChange({ ...EMPTY_FILTERS, sort: filters.sort })}
          >
            <Icon name="x" size={16} />
            Filtreleri temizle
          </button>
        ) : null}
      </div>
    </section>
  );
}

// ---- list -----------------------------------------------------------------------------

function Chip({ tone, children }: { tone: 'ok' | 'warn' | 'bad' | 'none'; children: string }) {
  return <span className={`pm-chip pm-chip--${tone}`}>{children}</span>;
}

function FitBar({ value, max, label }: { value: number; max: number; label: string }) {
  return (
    <div className="pm-fit">
      <span className="pm-fit__label">{label}</span>
      <Meter value={max > 0 ? value / max : 0} height={4} label={`${label} ${value} / ${max}`} />
      <span className="pm-fit__value mono">
        {value}/{max}
      </span>
    </div>
  );
}

function ProspectRowHead({
  row,
  now,
  open,
  onToggle,
}: {
  row: ProspectRow;
  now: number;
  open: boolean;
  onToggle: () => void;
}) {
  const foundAt = row.found.at ?? row.firstSeenAt;
  return (
    <button type="button" className="pm-row__head" aria-expanded={open} onClick={onToggle}>
      <span className="pm-cell pm-cell--name">
        <span className="pm-name">{row.name}</span>
        <span className="pm-sub">
          {[
            row.domain,
            [
              row.locality,
              row.state === null
                ? null
                : row.stateBasis === 'scan'
                  ? `${row.state} (tarama bölgesi)`
                  : row.stateBasis === 'registry'
                    ? `${row.state} (ABN sicili)`
                    : row.stateBasis === 'name'
                      ? `${row.state} (firma adından)`
                      : row.state,
            ]
              .filter(Boolean)
              .join(' '),
            countryLabel(row.country),
          ]
            .filter((part) => part !== null && part !== '')
            .join(' · ')}
        </span>
      </span>
      <span className="pm-cell pm-cell--kind">
        <span>{row.kindLabel}</span>
        {row.roleLabel && row.role !== 'unknown' ? <span className="pm-sub">{row.roleLabel}</span> : null}
        <Chip tone={productionTone(row.production)}>{row.productionLabel}</Chip>
      </span>
      <span className="pm-cell pm-cell--stage">
        <Chip tone={stageTone(row.stage)}>{STAGE_LABELS_TR[row.stage] ?? row.stage}</Chip>
        <span className="pm-fitnum mono" title="Uyum puanı (100 üzerinden)">
          {row.fit.total}
        </span>
      </span>
      <span className="pm-cell pm-cell--found">
        <span className="mono">{when(foundAt, now)}</span>
        <span className="pm-sub">
          {providerLabel(sourceKey(row))}
          {row.found.query ? ` · ${row.found.query}` : ''}
          {row.hits > 1 ? ` · ${row.hits} aramada` : ''}
        </span>
      </span>
      <span className="pm-cell pm-cell--decision">
        {row.decision === null ? (
          <Chip tone="none">Karar yok</Chip>
        ) : (
          <Chip tone={verdictTone(row.decision.verdict)}>{row.decision.verdictLabel}</Chip>
        )}
        <span className="pm-sub pm-clip">
          {row.decision === null
            ? 'doğrulama sırasında'
            : (row.decision.gate ?? row.decision.reasons[0] ?? stamp(row.decision.at))}
        </span>
      </span>
      <span className="pm-cell pm-cell--mail">
        {row.contact.sent > 0 ? (
          <Chip tone="ok">{`Mail gitti (${row.contact.sent})`}</Chip>
        ) : (row.contact.drafts ?? 0) > 0 ? (
          <Chip tone="warn">{`${row.contact.drafts} mail hazır`}</Chip>
        ) : (
          <Chip tone="none">Mail gitmedi</Chip>
        )}
        <span className="pm-sub">
          {row.contact.emails > 0 ? `${row.contact.emails} e-posta` : 'e-posta yok'}
          {row.contact.phones > 0 ? ` · ${row.contact.phones} telefon` : ''}
          {row.contact.lastReplyAt !== null ? ' · cevap verdi' : ''}
        </span>
      </span>
      <Icon name={open ? 'chevron-up' : 'chevron-down'} size={18} className="pm-row__chev" />
    </button>
  );
}

function DetailSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="pm-detail__sec">
      <h3 className="pm-detail__title">{title}</h3>
      {children}
    </section>
  );
}

function Facts({ items }: { items: readonly [string, ReactNode][] }) {
  return (
    <dl className="pm-facts">
      {items.map(([term, value]) => (
        <div key={term} className="pm-facts__row">
          <dt>{term}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function ExternalLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="pm-link">
      {children}
      <Icon name="external-link" size={14} />
    </a>
  );
}

function IdentitySection({ row }: { row: ProspectRow }) {
  const map =
    row.lat !== null && row.lon !== null
      ? `https://www.google.com/maps?q=${row.lat.toFixed(5)},${row.lon.toFixed(5)}`
      : null;
  const items: [string, ReactNode][] = [
    [
      'Web sitesi',
      row.website ? (
        <ExternalLink key="site" href={row.website}>
          {row.domain ?? row.website}
        </ExternalLink>
      ) : (
        'yok'
      ),
    ],
    ['Site kontrolü', row.domainCheck ? (DOMAIN_CHECK_LABELS_TR[row.domainCheck] ?? row.domainCheck) : 'yok'],
  ];
  if (row.legalName) items.push(['Yasal ad', row.legalName]);
  items.push([
    'Sicil',
    row.registryId ? `${row.registry?.toUpperCase() ?? ''} ${row.registryId}`.trim() : 'sicil kaydı henüz eşleşmedi',
  ]);
  items.push([
    'Adres',
    <span key="address">
      {[row.address, row.locality, row.state, row.postcode, countryLabel(row.country)].filter(Boolean).join(', ') ||
        'yok'}
      {map ? (
        <>
          {' '}
          <ExternalLink href={map}>haritada aç</ExternalLink>
        </>
      ) : null}
    </span>,
  ]);
  if (row.role && row.role !== 'unknown') items.push(['Kapının rol kararı', row.roleLabel ?? row.role]);
  items.push([
    'Lead no',
    <span key="lead" className="mono">
      {row.leadId}
    </span>,
  ]);
  return (
    <DetailSection title="Kimlik">
      <Facts items={items} />
    </DetailSection>
  );
}

function WhySection({ row }: { row: ProspectRow }) {
  return (
    <DetailSection title="Neden potansiyel">
      <p className="pm-why">{row.why}</p>
      <Facts
        items={[
          ['Tür', `${row.kindLabel} · ${basisText(row.kindBasis, row.kindMatched)}`],
          [
            'Üretim',
            `${row.productionLabel} · ${row.productionBasis === 'guess' ? 'türden tahmin' : basisText(row.productionBasis)}`,
          ],
          ...(row.makes ? ([['Ne yapıyor', row.makes]] as [string, ReactNode][]) : []),
          ...(row.tier ? ([['Öncelik', row.tier]] as [string, ReactNode][]) : []),
          ...(row.confidence !== null ? ([['Kimlik güveni', `${row.confidence} / 100`]] as [string, ReactNode][]) : []),
          ...(row.unfitReason
            ? ([['Uygun değil', UNFIT_LABELS_TR[row.unfitReason] ?? row.unfitReason]] as [string, ReactNode][])
            : []),
        ]}
      />
      <div className="pm-fits" title={`Uyum puanı ${row.fit.total} / 100`}>
        <FitBar label="Araç" value={row.fit.vehicle} max={40} />
        <FitBar label="Pazar" value={row.fit.market} max={20} />
        <FitBar label="Tasarım" value={row.fit.design} max={20} />
        <FitBar label="Büyüklük" value={row.fit.size} max={10} />
        <FitBar label="Boşluk" value={row.fit.gap} max={10} />
        <FitBar label="Toplam" value={row.fit.total} max={100} />
      </div>
    </DetailSection>
  );
}

function FoundSection({ detail, now }: { detail: ProspectDetail; now: number }) {
  return (
    <DetailSection title={`Nasıl bulundu (${detail.found.length} arama)`}>
      {detail.found.length === 0 ? (
        <p className="pm-muted">
          Arama kaydı yok; firma {providerLabel(detail.row.source)} kaynağından eklendi
          {detail.row.sourceRef ? ` (${detail.row.sourceRef})` : ''}.
        </p>
      ) : (
        <div className="pm-scroll">
          <table className="pm-table">
            <thead>
              <tr>
                <th scope="col">Gün</th>
                <th scope="col">Kaynak</th>
                <th scope="col">Arama</th>
                <th scope="col">Yer</th>
                <th scope="col" className="num">
                  Sıra
                </th>
                <th scope="col">Sonuç</th>
              </tr>
            </thead>
            <tbody>
              {detail.found.map((hit) => (
                <tr key={`${hit.at ?? 0}-${hit.provider}-${hit.query}-${hit.rank ?? 0}`}>
                  <td className="mono">{when(hit.at, now)}</td>
                  <td>{providerLabel(hit.provider)}</td>
                  <td>
                    {hit.query}
                    {hit.family ? <span className="pm-muted"> · aile {hit.family}</span> : null}
                  </td>
                  <td>{hit.locality ?? ''}</td>
                  <td className="num mono">{hit.rank ?? ''}</td>
                  <td>
                    {hit.url ? <ExternalLink href={hit.url}>sonuç</ExternalLink> : null}
                    {hit.filter ? <span className="pm-muted"> {HIT_FILTER_TR[hit.filter] ?? hit.filter}</span> : null}
                    {hit.resolution ? (
                      <span className="pm-muted"> · {HIT_RESOLUTION_TR[hit.resolution] ?? hit.resolution}</span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DetailSection>
  );
}

function DecisionSection({
  detail,
  nextVerify,
  now,
}: {
  detail: ProspectDetail;
  nextVerify: number | null;
  now: number;
}) {
  if (detail.decisions.length === 0) {
    return (
      <DetailSection title="Doğrulama">
        <p className="pm-muted">
          Henüz doğrulanmadı; doğrulama sırasında.
          {nextVerify !== null ? ` Bir sonraki doğrulama koşusu ${when(nextVerify, now)}.` : ''}
        </p>
      </DetailSection>
    );
  }
  return (
    <DetailSection title={`Doğrulama (${detail.decisions.length} karar)`}>
      <ol className="pm-decisions">
        {detail.decisions.map((decision) => (
          <li key={decision.id} className="pm-decision">
            <div className="pm-decision__head">
              <Chip tone={verdictTone(decision.verdict)}>{decision.verdictLabel}</Chip>
              <span className="mono">{stamp(decision.at)}</span>
              <span className="pm-muted">
                {decision.by ? (DECIDER_LABELS_TR[decision.by] ?? decision.by) : ''}
                {decision.modelFamily ? ` (${decision.modelFamily})` : ''}
                {decision.confidence !== null ? ` · güven ${decision.confidence}` : ''}
                {` · ${decision.evidence} kanıt`}
                {decision.applied ? ' · uygulandı' : ' · uygulanmadı'}
              </span>
            </div>
            <ul className="pm-reasons">
              {decision.reasons.map((reason) => (
                <li key={`${reason.code}-${reason.detail}`} className="pm-reason">
                  <span className="pm-reason__code mono">{reason.code}</span>
                  <span className="pm-reason__text">
                    {reason.labelTr}
                    {reason.detail && reason.detail !== reason.labelTr ? (
                      <span className="pm-muted pm-reason__raw"> {reason.detail}</span>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ol>
    </DetailSection>
  );
}

function EvidenceSection({ detail }: { detail: ProspectDetail }) {
  const [all, setAll] = useState(false);
  const list = all ? detail.evidence : detail.evidence.slice(0, 12);
  if (detail.evidence.length === 0) {
    return (
      <DetailSection title="Kanıtlar">
        <p className="pm-muted">Henüz kanıt toplanmadı; doğrulama sitesini ve sicili okuyunca eklenir.</p>
      </DetailSection>
    );
  }
  return (
    <DetailSection title={`Kanıtlar (${detail.evidence.length})`}>
      <ul className="pm-evidence">
        {list.map((item) => (
          <li key={item.id} className="pm-evidence__item">
            <span
              className={`pm-chip pm-chip--${item.polarity === 'negative' ? 'bad' : item.polarity === 'positive' ? 'ok' : 'none'}`}
            >
              {item.polarity === 'negative' ? 'aleyhte' : item.polarity === 'positive' ? 'lehte' : 'nötr'}
            </span>
            <span className="pm-evidence__body">
              <span>
                <strong>{CLAIM_LABELS_TR[item.claim] ?? item.claim}</strong>
                {item.group ? <span className="pm-muted"> · {item.group}</span> : null}
                {item.at !== null ? <span className="pm-muted"> · {stamp(item.at)}</span> : null}
              </span>
              {item.phrase ? <span className="pm-quote">"{item.phrase}"</span> : null}
              {item.title || item.snippet ? (
                <span className="pm-muted">{[item.title, item.snippet].filter(Boolean).join(' · ')}</span>
              ) : null}
              {item.url ? <ExternalLink href={item.url}>{item.domain ?? 'kaynak'}</ExternalLink> : null}
            </span>
          </li>
        ))}
      </ul>
      {detail.evidence.length > list.length ? (
        <button type="button" className="btn btn--secondary" onClick={() => setAll(true)}>
          Hepsini göster ({detail.evidence.length})
        </button>
      ) : null}
    </DetailSection>
  );
}

/** v0.1.118: copies a draft's subject and English text for a hand send. */
/** v0.1.127: the outreach lines of a company and which mail goes first, in Turkish. */
function PlanNote({ plan }: { plan: NonNullable<ProspectDetail['plan']> }) {
  // v0.1.170: both mails are CadGaragePro's, signed by Sercan; the lines are told apart by what they offer.
  const reasons = [
    plan.partnerReason === null
      ? null
      : `İş birliği maili yok: ${OUTREACH_REASON_TR[plan.partnerReason] ?? plan.partnerReason}.`,
    plan.scanReason === null
      ? null
      : `CAD dosyaları maili yok: ${OUTREACH_REASON_TR[plan.scanReason] ?? plan.scanReason}.`,
  ].filter((line): line is string => line !== null);
  return (
    <div className="pm-note pm-plan">
      <strong>{OUTREACH_ARM_TR[plan.arm] ?? plan.arm}</strong>
      {plan.arm === 'partner_first' || plan.arm === 'scan_first' ? (
        <span>
          {' '}
          · iki mail de CadGaragePro adına Sercan imzasıyla gider; ikinci mail takiptir, ilk mailden 14 gün sonra ve
          firma cevap vermediyse gidebilir (takiplerin onay adımı ilk mailler gidince açılacak).
        </span>
      ) : null}
      {reasons.length > 0 ? <span> {reasons.join(' ')}</span> : null}
      {plan.offer.length > 0 ? (
        <span> Önerilecek taramalar: {plan.offer.map((pick) => pick.name).join(', ')}.</span>
      ) : null}
    </div>
  );
}

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState<boolean | null>(null);
  return (
    <button type="button" className="btn btn--secondary" onClick={() => void copyText(text).then(setCopied)}>
      <Icon name="copy" size={14} />
      {copied === true ? 'Kopyalandı' : copied === false ? 'Kopyalanamadı' : label}
    </button>
  );
}

function ContactSection({ detail }: { detail: ProspectDetail }) {
  const hasEmail = detail.channels.some((channel) => channel.type === 'generic_email' || channel.type === 'role_email');
  return (
    <DetailSection title="Temas ve mailler">
      {detail.channels.length === 0 ? (
        <p className="pm-muted">İletişim kanalı bulunmadı.</p>
      ) : (
        <ul className="pm-channels">
          {detail.channels.map((channel, index) => (
            <li key={`${channel.type}-${channel.value ?? channel.url ?? index}`} className="pm-channel">
              <span className="pm-channel__type">{CHANNEL_LABELS_TR[channel.type] ?? channel.type}</span>
              {channel.value !== null ? <span className="mono pm-channel__value">{channel.value}</span> : null}
              <span className="pm-muted">
                {channel.status ? (CHANNEL_STATUS_TR[channel.status] ?? channel.status) : ''}
                {channel.mxOk === true
                  ? ' · posta sunucusu var'
                  : channel.mxOk === false
                    ? ' · posta sunucusu yok'
                    : ''}
                {channel.noUnsolicited ? ' · istenmeyen ileti kabul etmiyor' : ''}
              </span>
              {channel.url ? <ExternalLink href={channel.url}>bulunduğu sayfa</ExternalLink> : null}
            </li>
          ))}
        </ul>
      )}
      {detail.plan ? <PlanNote plan={detail.plan} /> : null}
      {detail.messages.some((message) => message.status === 'draft') ? (
        <p className="pm-note">
          Taslaklar hazır, hiçbiri gönderilmedi. İngilizce metin gidecek olan; Türkçesi senin okuman için. Gönderim
          kutuları hazır olunca her biri senin onayınla gider.
        </p>
      ) : null}
      {detail.messages.length === 0 ? (
        <p className="pm-note">
          Bu firmaya henüz mail gitmedi.{' '}
          {hasEmail
            ? 'Gönderim kapalı: Zoho kutuları hazır olunca açılacak; her mail senin onayınla gider.'
            : 'Önce bir e-posta adresi bulunmalı.'}
        </p>
      ) : (
        <ul className="pm-messages">
          {detail.messages.map((message) => (
            <li key={message.id} className="pm-message">
              <details>
                <summary className="pm-message__head">
                  <span className="pm-message__subject">
                    {message.campaign ? `${message.campaign}: ` : ''}
                    {message.subject ?? '(konu yok)'}
                  </span>
                  <span className="pm-muted">
                    {message.to ??
                      (message.deliveryMode ? (DELIVERY_MODE_TR[message.deliveryMode] ?? message.deliveryMode) : '')}
                    {message.step === 1 ? ' · ilk mail' : ''}
                    {message.step === 2 ? ' · ikinci mail (cevap gelince ya da 14 gün sonra)' : ''}
                    {' · '}
                    {message.status ? (MESSAGE_STATUS_TR[message.status] ?? message.status) : ''}
                    {message.blockReason ? ` (${BLOCK_REASON_TR[message.blockReason] ?? message.blockReason})` : ''}
                    {message.sentAt !== null
                      ? ` · ${stamp(message.sentAt)}`
                      : message.scheduledFor !== null
                        ? ` · planlanan ${stamp(message.scheduledFor)}`
                        : ''}
                  </span>
                </summary>
                {message.subjectTr ? <p className="pm-muted">Konu (TR): {message.subjectTr}</p> : null}
                <pre className="pm-message__body">{message.body ?? ''}</pre>
                {message.body ? (
                  <CopyButton text={`${message.subject ?? ''}\n\n${message.body}`} label="Konu ve metni kopyala" />
                ) : null}
                {message.bodyTr ? (
                  <details className="pm-message__tr">
                    <summary className="pm-muted">Türkçesi</summary>
                    <pre className="pm-message__body">{message.bodyTr}</pre>
                  </details>
                ) : null}
              </details>
            </li>
          ))}
        </ul>
      )}
    </DetailSection>
  );
}

function HistorySection({ detail }: { detail: ProspectDetail }) {
  return (
    <DetailSection title="Geçmiş">
      {detail.history.length === 0 ? (
        <p className="pm-muted">Aşama değişikliği yok.</p>
      ) : (
        <ol className="pm-history">
          {detail.history.map((item) => (
            <li key={`${item.at ?? 0}-${item.to}`}>
              <span className="mono">{stamp(item.at)}</span>{' '}
              {item.from === null
                ? `${STAGE_LABELS_TR[item.to] ?? item.to} olarak eklendi`
                : `${STAGE_LABELS_TR[item.from] ?? item.from} → ${STAGE_LABELS_TR[item.to] ?? item.to}`}
              <span className="pm-muted">
                {' '}
                · {item.actor}
                {item.reason ? ` · ${UNFIT_LABELS_TR[item.reason] ?? item.reason}` : ''}
              </span>
            </li>
          ))}
        </ol>
      )}
    </DetailSection>
  );
}

function ProspectDetailPanel({
  row,
  state,
  nextVerify,
  now,
  onRetry,
}: {
  row: ProspectRow;
  state: ProspectsState;
  nextVerify: number | null;
  now: number;
  onRetry: () => void;
}) {
  const detail = state.details[row.leadId];
  const error = state.detailErrors[row.leadId];
  return (
    <div className="pm-detail">
      <div className="pm-detail__grid">
        <IdentitySection row={row} />
        <WhySection row={row} />
      </div>
      {detail === undefined ? (
        error !== undefined ? (
          <p className="pm-muted" role="status">
            Ayrıntı yüklenemedi: {error}{' '}
            <button type="button" className="btn btn--secondary" onClick={onRetry}>
              Tekrar dene
            </button>
          </p>
        ) : (
          <p className="pm-muted" role="status">
            Aramalar, kanıtlar ve mailler yükleniyor…
          </p>
        )
      ) : (
        <>
          <FoundSection detail={detail} now={now} />
          <DecisionSection detail={detail} nextVerify={nextVerify} now={now} />
          <div className="pm-detail__grid">
            <ContactSection detail={detail} />
            <HistorySection detail={detail} />
          </div>
          <EvidenceSection detail={detail} />
        </>
      )}
    </div>
  );
}

// ---- page -----------------------------------------------------------------------------

export function ProspectsPage() {
  const { store, state } = useProspects();
  const now = usePanel().now;
  const [filters, setFilters] = useState<ProspectFilters>(EMPTY_FILTERS);
  const [dim, setDim] = useState<GroupDim>('kind');
  const [open, setOpen] = useState<string | null>(null);
  const [limit, setLimit] = useState(PAGE_SIZE);

  useEffect(() => store.watch(), [store]);

  const rows = state.view?.rows ?? [];
  const shown = useMemo(() => {
    const filtered = filterProspects(rows, filters);
    return sortProspects(filtered, filters.sort);
  }, [rows, filters]);

  const change = (patch: Partial<ProspectFilters>) => {
    setFilters((current) => ({ ...current, ...patch }));
    setLimit(PAGE_SIZE);
  };
  const pick = (which: GroupDim, key: string | null) => {
    setFilters((current) => {
      const picks = { ...current.picks };
      if (key === null) delete picks[which];
      else picks[which] = key;
      return { ...current, picks };
    });
    setLimit(PAGE_SIZE);
  };
  const toggle = (leadId: string) => {
    if (open === leadId) {
      setOpen(null);
      return;
    }
    setOpen(leadId);
    void store.loadDetail(leadId);
  };

  if (state.view === null) {
    return (
      <div className="shop-status" role="status">
        {state.error !== null ? (
          <>
            Potansiyel müşteriler yüklenemedi: {state.error}{' '}
            <button type="button" className="btn btn--secondary" onClick={() => void store.reload()}>
              Tekrar dene
            </button>
          </>
        ) : (
          'Yükleniyor…'
        )}
      </div>
    );
  }
  const view = state.view;
  const nextVerify = view.jobs.next.find((job) => job.kind === 'doğrulama')?.at ?? null;

  return (
    <div className="pm-page">
      <LiveBar
        state={state}
        shown={shown.length}
        onReload={() => void store.reload()}
        onExport={() => download(shown, now)}
      />
      <SummaryBoard view={view} />
      <JobsPanel view={view} now={now} />
      <GroupsPanel rows={rows} filters={filters} dim={dim} onDim={setDim} onPick={pick} />
      <FiltersBar rows={rows} filters={filters} shown={shown.length} onChange={change} onPick={pick} />
      <div className="pm-rows">
        <div className="pm-rows__head" aria-hidden="true">
          <span>Firma</span>
          <span>Tür ve üretim</span>
          <span>Aşama · uyum</span>
          <span>Nereden, ne zaman</span>
          <span>Doğrulama</span>
          <span>Temas</span>
          <span />
        </div>
        {shown.length === 0 ? (
          <p className="pm-muted pm-rows__empty">Bu filtrelerle firma yok.</p>
        ) : (
          <ul className="pm-rows__list" aria-label="Firmalar">
            {shown.slice(0, limit).map((row) => (
              <li key={row.leadId} className={open === row.leadId ? 'pm-row pm-row--open' : 'pm-row'}>
                <ProspectRowHead row={row} now={now} open={open === row.leadId} onToggle={() => toggle(row.leadId)} />
                {open === row.leadId ? (
                  <ProspectDetailPanel
                    row={row}
                    state={state}
                    nextVerify={nextVerify}
                    now={now}
                    onRetry={() => void store.loadDetail(row.leadId, true)}
                  />
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {shown.length > limit ? (
          <button type="button" className="btn btn--secondary pm-more" onClick={() => setLimit(limit + PAGE_SIZE)}>
            {formatNumber(Math.min(PAGE_SIZE, shown.length - limit))} firma daha göster (
            {formatNumber(shown.length - limit)} kaldı)
          </button>
        ) : null}
      </div>
      <p className="pm-foot pm-muted">
        {formatNumber(view.summary.total)} firmanın {formatNumber(rows.filter(isQualified).length)} tanesi nitelikli ve
        üstü. Karar verilmemiş firmalar doğrulama sırasında bekler; kapı her saat başı buçukta çalışır. Excel dosyası
        ekrandaki filtreleri uygular.
      </p>
    </div>
  );
}
